import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { useOrg } from '../app/OrgProvider';
import { useAuth } from '../auth/AuthProvider';
import { AppLink } from '../components/Nav';
import { DeleteAccount } from '../components/DeleteAccount';
import { ProfilePicture } from '../components/ProfilePicture';
import { ThemePicker } from '../components/ThemePicker';
import { Badge, Banner, Button, CopyButton, ErrorText, Input, PageHeader, cx } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { config } from '../lib/env';
import { shortDate } from '../lib/format';
import { paywallPath } from '../lib/plans';
import { fetchMe, updateMe } from '../queries/account';
import { fetchDeviceSessions, revokeDeviceSession, type DeviceSession } from '../queries/device';

/**
 * Settings: deliberately short. Your account comes from the identity provider
 * and your teams come from the API; there is nothing else to configure yet, and
 * inventing switches that do nothing would be worse than an honest gap.
 */

function providerLabel(providerId: string | undefined): string {
  switch (providerId) {
    case 'google.com':
      return 'Google';
    case 'password':
      return 'Email and password';
    case undefined:
      return 'Not recorded';
    default:
      return providerId;
  }
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-hairline py-3 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="w-44 shrink-0 text-caption text-chalk-faint">{label}</dt>
      <dd className="min-w-0 break-words text-body text-chalk">{children}</dd>
    </div>
  );
}

/**
 * Claude, Cursor and the other AI tools that can read your recordings, and
 * the desktops signed in as you. The address the tools connect to, how to
 * connect the common ones, and every connection that exists, each with a
 * way to end it. A Team feature: on the free plan the section says what it
 * is and where Team is, which teaches more than a missing section would.
 */
function ConnectedApps({ paid }: { paid: boolean }) {
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: ['device-sessions'], queryFn: fetchDeviceSessions, enabled: paid });
  const revoke = useMutation({
    mutationFn: (id: string) => revokeDeviceSession(id),
    onSuccess: (_data, id) => {
      const gone = sessions.data?.sessions.find((s) => s.id === id);
      track('Connected App Revoked', { kind: gone?.kind ?? 'unknown' });
      void queryClient.invalidateQueries({ queryKey: ['device-sessions'] });
    },
  });
  const mcpUrl = `${config.apiUrl}/mcp`;
  const apps = sessions.data?.sessions.filter((s) => s.kind === 'app') ?? [];
  const desktops = sessions.data?.sessions.filter((s) => s.kind === 'desktop') ?? [];

  const row = (session: DeviceSession) => (
    <li key={session.id} className="flex flex-wrap items-center gap-3 border-b border-hairline py-3 last:border-b-0">
      <div className="min-w-0 flex-1">
        <p className="truncate text-body text-chalk">{session.label}</p>
        <p className="text-caption text-chalk-faint tnum">
          {session.lastUsedAt ? `Last used ${shortDate(session.lastUsedAt)}` : 'Not used yet'}
          {' · '}connected {shortDate(session.createdAt)}
        </p>
      </div>
      <Button
        size="sm"
        variant="danger"
        busy={revoke.isPending && revoke.variables === session.id}
        onClick={() => revoke.mutate(session.id)}
      >
        Disconnect
      </Button>
    </li>
  );

  return (
    <section className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-h4 text-chalk">Claude and other AI tools</h2>
          <p className="mt-1 max-w-prose text-caption text-chalk-dim">
            Hand an AI tool the link to a recording and it can read the transcript, see which page was on screen
            at each moment, take screenshots, and tag or comment on it, as you. Slate is an MCP server; anything
            that speaks MCP connects.
          </p>
        </div>
        {!paid ? <Badge tone="info">Team</Badge> : null}
      </div>

      {!paid ? (
        <div className="mt-4 rounded-lg border border-hairline bg-slate-900/60 p-4">
          <p className="text-body text-chalk">Connecting AI tools comes with Team.</p>
          <p className="mt-1 text-caption text-chalk-dim">
            Along with every recording kept, transcripts, and the editor. Half the price of Loom.
          </p>
          <AppLink to={paywallPath('mcp')} onClick={() => track('Paywall Opened', { feature: 'mcp' })}>
            <Button variant="primary" size="sm" className="mt-3">
              See the Team plan
            </Button>
          </AppLink>
        </div>
      ) : (
        <>
          <div className="mt-4 rounded-lg border border-hairline bg-slate-900/60 p-4">
            <p className="text-caption text-chalk-faint">Server address</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-slate-950 px-2 py-1 font-mono text-caption text-chalk">
                {mcpUrl}
              </code>
              <CopyButton value={mcpUrl} label="Copy" size="sm" onCopied={() => track('MCP URL Copied')} />
            </div>
            <dl className="mt-3 space-y-2 text-caption text-chalk-dim">
              <div>
                <dt className="text-chalk">Claude Code</dt>
                <dd className="mt-0.5">
                  <code className="rounded bg-slate-950 px-1.5 py-0.5 font-mono text-micro text-chalk">
                    claude mcp add --transport http slate {mcpUrl}
                  </code>
                  {' '}then <code className="font-mono">/mcp</code> to sign in.
                </dd>
              </div>
              <div>
                <dt className="text-chalk">Claude.ai and Claude Desktop</dt>
                <dd className="mt-0.5">Settings, Connectors, Add custom connector, paste the address.</dd>
              </div>
              <div>
                <dt className="text-chalk">Cursor and others</dt>
                <dd className="mt-0.5">Add an MCP server with that URL. Each asks you to sign in here, once.</dd>
              </div>
            </dl>
          </div>

          <div className="mt-5">
            <h3 className="text-caption font-medium text-chalk-dim">Connected apps</h3>
            {sessions.isError ? (
              <div className="mt-2">
                <ErrorText>{errorMessage(sessions.error)}</ErrorText>
              </div>
            ) : apps.length === 0 ? (
              <p className="mt-2 text-caption text-chalk-faint">Nothing connected yet.</p>
            ) : (
              <ul className="mt-1">{apps.map(row)}</ul>
            )}
          </div>
          {desktops.length > 0 ? (
            <div className="mt-5">
              <h3 className="text-caption font-medium text-chalk-dim">Signed-in desktops</h3>
              <ul className="mt-1">{desktops.map(row)}</ul>
            </div>
          ) : null}
          {revoke.isError ? (
            <div className="mt-2">
              <ErrorText>{errorMessage(revoke.error)}</ErrorText>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/** The tabs. Each is its own route so the address means something. */
export function SettingsTabs({
  active,
}: {
  active: 'account' | 'subscription' | 'configuration' | 'notifications' | 'branding' | 'domain';
}) {
  const { org, role } = useOrg();
  // A plain member has nothing on the Subscription tab: the plan is the
  // owner's, the roster is the admins'. Individuals still get it, since for
  // them it is the purchase page.
  const plainMember = org !== null && role === 'user';
  const tabs = [
    { key: 'account', label: 'Account', to: '/settings' },
    ...(plainMember ? [] : [{ key: 'subscription', label: 'Subscription', to: '/settings/subscription' }]),
    // Before branding: how recordings behave is answered more often than how
    // they look, and everyone has it, team or not.
    { key: 'configuration', label: 'Configuration', to: '/settings/configuration' },
    // What happens to a recording after it is made, and whether to hear
    // about it: next to how it starts.
    { key: 'notifications', label: 'Notifications', to: '/settings/notifications' },
    // Shown to everyone, team or not: the page itself explains that branding
    // belongs to a team, which is a better answer than a tab that is missing
    // for reasons nobody can see.
    { key: 'branding', label: 'Branding', to: '/settings/branding' },
    // Its own tab: the rest of branding is chosen in a sitting, this is a walk
    // with waiting in it, usually done by whoever looks after DNS.
    { key: 'domain', label: 'Domain', to: '/settings/domain' },
  ] as const;
  return (
    <nav className="mb-6 flex gap-6 border-b border-hairline" aria-label="Settings sections">
      {tabs.map((tab) => (
        <AppLink
          key={tab.key}
          to={tab.to}
          aria-current={tab.key === active ? 'page' : undefined}
          className={cx(
            '-mb-px border-b-2 pb-3 text-body transition-colors duration-120',
            tab.key === active ? 'border-chalk font-medium text-chalk' : 'border-transparent text-chalk-dim hover:text-chalk',
          )}
        >
          {tab.label}
        </AppLink>
      ))}
    </nav>
  );
}

export function SettingsPage() {
  const { user, signOut } = useAuth();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled: user !== null });
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  useEffect(() => {
    track('Viewed Settings', { tab: 'account' });
  }, []);
  useEffect(() => {
    if (me.data) setName(me.data.name ?? '');
  }, [me.data]);
  const dirty = (me.data?.name ?? '') !== name.trim();
  const rename = useMutation({
    mutationFn: () => updateMe({ name: name.trim() || null }),
    onSuccess: (next) => {
      track('Profile Updated', { field: 'name' });
      queryClient.setQueryData(['me'], next);
    },
  });

  const leave = useMutation({ mutationFn: () => signOut() });

  if (!user) {
    return (
      <>
        <PageHeader title="Settings" />
        <Banner tone="info" title="You are signed out">
          Sign in again to see your account details.
        </Banner>
      </>
    );
  }

  const created = user.metadata.creationTime;

  return (
    <>
      <PageHeader title="Settings" subtitle="Your account, your plan and your team." />
      <SettingsTabs active="account" />

      <div className="space-y-6">
        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Account</h2>
          <p className="mt-1 text-caption text-chalk-dim">
            Your name and picture are yours to change. Email and password belong to the provider.
          </p>
          {me.data ? (
            <div className="mt-4">
              <ProfilePicture me={me.data} />
            </div>
          ) : null}
          <form
            className="mt-4 max-w-xs space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate();
            }}
          >
            <Input label="Name" value={name} maxLength={80} onChange={(event) => setName(event.target.value)} />
            <div className="flex items-center gap-3">
              <Button type="submit" variant="primary" size="sm" busy={rename.isPending} disabled={!dirty}>
                Save
              </Button>
              {rename.isSuccess && !dirty ? <span className="text-caption text-go">Saved</span> : null}
            </div>
            {rename.isError ? <ErrorText>{errorMessage(rename.error)}</ErrorText> : null}
          </form>
          <dl className="mt-3">
            <Row label="Email">{user.email ?? 'No email on this account'}</Row>
            <Row label="Signs in with">
              {providerLabel(user.providerData[0]?.providerId)}
            </Row>
            <Row label="Account created">
              {created ? <span className="tnum">{shortDate(created)}</span> : 'Not recorded'}
            </Row>
          </dl>
        </section>

        {me.data ? <ConnectedApps paid={me.data.paidSeat} /> : null}

        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Appearance</h2>
          <p className="mt-1 text-caption text-chalk-dim">
            How Slate is lit in this browser. System follows your computer. Pages a team has branded keep the
            look the team chose.
          </p>
          <div className="mt-4">
            <ThemePicker />
          </div>
        </section>

        <section className="card p-4 sm:p-5">
          <h2 className="text-h4 text-chalk">Sign out</h2>
          <p className="mt-1 text-body text-chalk-dim">
            Ends the session in this browser. Your recordings and share links are unaffected.
          </p>
          <Button
            variant="danger"
            className="mt-4"
            busy={leave.isPending}
            onClick={() => leave.mutate()}
          >
            Sign out
          </Button>
          {leave.isError ? (
            <div className="mt-2">
              <ErrorText>{errorMessage(leave.error)}</ErrorText>
            </div>
          ) : null}
        </section>

        <DeleteAccount />
      </div>
    </>
  );
}
